import { Controller } from "stimulus";
import { post, patch, destroy } from "@rails/request.js";

// Drives the reusable files manager (folders + files): grade/lista toggle,
// search, upload, drag-to-move, context menu, preview panel and the new-folder
// modal. Persistence happens server-side via Turbo Stream; view/search/preview
// stay client-side for an instant feel. See Files::ManagerComponent.
export default class extends Controller {
  static targets = [
    "search", "viewSeg", "fileInput", "folderModal", "folderName", "swatches", "item",
  ];
  static values = {
    recordType: String,
    recordId: Number,
    currentFolderId: Number,
    uploadUrl: String,
    createUrl: String,
    maxFileSize: Number,
    maxFileSizeMessage: String,
  };

  connect() {
    this.pickedColor = null;
    this.draggedItem = null;
    this.dragDepth = 0;

    const savedView = localStorage.getItem("files-manager:view") || "grade";
    this.applyView(savedView);

    // OS file drop onto the whole area
    this.onAreaDragEnterHandler = this.onAreaDragEnter.bind(this);
    this.onAreaDragOverHandler = (event) => { if (this.hasFiles(event)) event.preventDefault(); };
    this.onAreaDragLeaveHandler = this.onAreaDragLeave.bind(this);
    this.onAreaDropHandler = this.onAreaDrop.bind(this);
    this.element.addEventListener("dragenter", this.onAreaDragEnterHandler);
    this.element.addEventListener("dragover", this.onAreaDragOverHandler);
    this.element.addEventListener("dragleave", this.onAreaDragLeaveHandler);
    this.element.addEventListener("drop", this.onAreaDropHandler);

    // close context menu on outside click / Esc
    this.onDocumentClickHandler = (event) => { if (!event.target.closest(".fm-ctx")) this.closeMenu(); };
    this.onKeydownHandler = (event) => {
      if (event.key === "Escape") { this.closeMenu(); this.closeFolderModal(); }
    };
    document.addEventListener("click", this.onDocumentClickHandler);
    document.addEventListener("keydown", this.onKeydownHandler);
  }

  disconnect() {
    this.element.removeEventListener("dragenter", this.onAreaDragEnterHandler);
    this.element.removeEventListener("dragover", this.onAreaDragOverHandler);
    this.element.removeEventListener("dragleave", this.onAreaDragLeaveHandler);
    this.element.removeEventListener("drop", this.onAreaDropHandler);
    document.removeEventListener("click", this.onDocumentClickHandler);
    document.removeEventListener("keydown", this.onKeydownHandler);
    this.closeMenu();
  }

  // ---------- view switch ----------
  setView(event) { this.applyView(event.currentTarget.dataset.view); }

  applyView(view) {
    this.element.dataset.fmView = view;
    localStorage.setItem("files-manager:view", view);
    this.viewSegTargets.forEach((segment) => segment.setAttribute("aria-pressed", String(segment.dataset.view === view)));
  }

  // ---------- search ----------
  search() {
    const query = (this.searchTarget.value || "").toLowerCase().trim();
    this.itemTargets.forEach((item) => {
      const name = (item.dataset.fileName || item.dataset.folderName || "").toLowerCase();
      item.style.display = !query || name.includes(query) ? "" : "none";
    });
  }

  // ---------- navigation / open ----------
  onItemClick(event) {
    if (event.target.closest('[data-files-manager-target~="menuButton"]')) return; // handled by openMenu
    const item = event.currentTarget;
    if (item.dataset.itemKind === "folder") this.navigate(item.dataset.openUrl);
    else this.openPreview(item);
  }

  navigate(url) {
    const frame = this.element.closest("turbo-frame");
    if (frame && url) frame.src = url;
  }

  // ---------- upload ----------
  triggerUpload() { this.fileInputTarget.click(); }

  submitUpload() {
    const selectedFiles = this.fileInputTarget.files;
    if (!selectedFiles || !selectedFiles.length) return;
    this.uploadFiles(selectedFiles);
    this.fileInputTarget.value = "";
  }

  async uploadFiles(files) {
    const { accepted, rejected } = this.splitBySize(files);
    if (!accepted.length) {
      this.warnRejected(rejected);
      return;
    }

    const body = new FormData();
    body.append("record_type", this.recordTypeValue);
    body.append("record_id", this.recordIdValue);
    body.append("manager", "1");
    if (this.currentFolderIdValue) {
      body.append("attachment_folder_id", this.currentFolderIdValue);
      body.append("folder_id", this.currentFolderIdValue);
    }
    accepted.forEach((file) => body.append("record[attachments][]", file));

    this.toast(`Enviando ${accepted.length} ${accepted.length === 1 ? "arquivo" : "arquivos"}…`, true);
    await post(this.uploadUrlValue, { body, responseKind: "turbo-stream" });
    this.warnRejected(rejected);
  }

  // Files above the server limit (AttachmentSizeLimit) never leave the browser; the model validation
  // stays as the safety net. Without a configured limit everything goes through.
  splitBySize(files) {
    const limit = this.maxFileSizeValue || Infinity;
    const list = Array.from(files);
    return {
      accepted: list.filter((file) => file.size <= limit),
      rejected: list.filter((file) => file.size > limit),
    };
  }

  warnRejected(rejected) {
    if (!rejected.length) return;
    const names = rejected.map((file) => file.name).join(", ");
    this.toast(this.maxFileSizeMessageValue.replace("{{filename}}", names));
  }

  // ---------- drag to move (internal) ----------
  onDragStart(event) {
    this.draggedItem = event.currentTarget;
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", event.currentTarget.dataset.fileId || "");
  }

  onDragEnd() {
    this.draggedItem = null;
    this.element.querySelectorAll(".is-drop").forEach((node) => node.classList.remove("is-drop"));
  }

  onDragOver(event) {
    if (!this.draggedItem) return;
    event.preventDefault();
    event.currentTarget.classList.add("is-drop");
  }

  onDragLeave(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) event.currentTarget.classList.remove("is-drop");
  }

  onDrop(event) {
    if (!this.draggedItem) return;
    event.preventDefault();
    event.currentTarget.classList.remove("is-drop");
    const targetFolderId = event.currentTarget.dataset.folderId;
    this.moveFile(this.draggedItem.dataset.moveUrl, targetFolderId, this.draggedItem.dataset.fileName);
    this.draggedItem = null;
  }

  async moveFile(moveUrl, folderId, fileName) {
    const body = new FormData();
    if (folderId) body.append("attachment_folder_id", folderId);
    if (this.currentFolderIdValue) body.append("folder_id", this.currentFolderIdValue);
    await post(moveUrl, { body, responseKind: "turbo-stream" });
    if (fileName) this.toast(`“${fileName}” movido`);
  }

  // ---------- OS file drop on area ----------
  hasFiles(event) { return event.dataTransfer && Array.from(event.dataTransfer.types || []).includes("Files"); }

  onAreaDragEnter(event) {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    this.dragDepth++;
    this.element.classList.add("is-dragging");
  }

  onAreaDragLeave(event) {
    if (!this.hasFiles(event)) return;
    this.dragDepth--;
    if (this.dragDepth <= 0) { this.dragDepth = 0; this.element.classList.remove("is-dragging"); }
  }

  onAreaDrop(event) {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    this.dragDepth = 0;
    this.element.classList.remove("is-dragging");
    if (event.dataTransfer.files && event.dataTransfer.files.length) this.uploadFiles(event.dataTransfer.files);
  }

  // ---------- new folder modal ----------
  openFolderModal() {
    this.pickedColor = this.swatchesTarget.querySelector("[data-color]")?.dataset.color || null;
    this.folderNameTarget.value = "";
    this.folderModalTarget.dataset.open = "true";
    setTimeout(() => this.folderNameTarget.focus(), 60);
  }

  closeFolderModal() { delete this.folderModalTarget.dataset.open; }
  onModalBackdrop(event) { if (event.target === this.folderModalTarget) this.closeFolderModal(); }

  pickColor(event) {
    const swatch = event.currentTarget;
    this.pickedColor = swatch.dataset.color;
    this.swatchesTarget.querySelectorAll("[data-color]").forEach((candidate) => {
      candidate.classList.toggle("border-[color:var(--fm-text)]", candidate === swatch);
      candidate.classList.toggle("border-transparent", candidate !== swatch);
    });
  }

  async createFolder() {
    const folderName = (this.folderNameTarget.value || "").trim() || "Nova pasta";
    const body = new FormData();
    body.append("folderable_type", this.recordTypeValue);
    body.append("folderable_id", this.recordIdValue);
    if (this.currentFolderIdValue) body.append("folder_id", this.currentFolderIdValue);
    body.append("attachment_folder[name]", folderName);
    if (this.pickedColor) body.append("attachment_folder[color]", this.pickedColor);
    this.closeFolderModal();
    await post(this.createUrlValue, { body, responseKind: "turbo-stream" });
    this.toast(`Pasta “${folderName}” criada`);
  }

  // ---------- context menu ----------
  openMenu(event) {
    event.preventDefault();
    event.stopPropagation();
    const item = event.target.closest("[data-item-kind]");
    if (!item) return;
    this.closeMenu();

    const kind = item.dataset.itemKind;
    const menu = document.createElement("div");
    menu.className = "fm-ctx fixed z-[80] min-w-[184px] p-1 rounded-[10px] bg-white shadow-[0_1px_2px_rgba(26,24,22,0.06),0_6px_24px_-8px_rgba(26,24,22,0.10)] border border-[rgba(26,24,22,0.14)]";
    menu.innerHTML = kind === "folder" ? this.folderMenuHtml() : this.fileMenuHtml();
    document.body.appendChild(menu);
    this.menuElement = menu;

    this.bindMenuActions(menu, item, kind);
    this.positionMenu(menu, event.clientX || item.getBoundingClientRect().right, event.clientY || item.getBoundingClientRect().bottom);
  }

  positionMenu(menu, anchorX, anchorY) {
    menu.style.left = Math.min(anchorX, window.innerWidth - menu.offsetWidth - 8) + "px";
    menu.style.top = Math.min(anchorY, window.innerHeight - menu.offsetHeight - 8) + "px";
  }

  closeMenu() { if (this.menuElement) { this.menuElement.remove(); this.menuElement = null; } }

  menuItemHtml(action, label, danger = false) {
    const textColor = danger ? "color:var(--fm-accent,#c85a3e)" : "color:#1a1816";
    return `<button type="button" data-act="${action}" class="fm-ctx-item flex w-full items-center gap-[10px] px-[10px] py-[7px] rounded-md text-[12.5px] text-left hover:bg-[rgba(26,24,22,0.04)]" style="${textColor}">${label}</button>`;
  }

  fileMenuHtml() {
    return (
      this.menuItemHtml("preview", "Pré-visualizar") +
      this.menuItemHtml("download", "Baixar") +
      `<div class="h-px bg-[rgba(26,24,22,0.08)] my-1 mx-[6px]"></div>` +
      this.menuItemHtml("move", "Mover para…") +
      `<div class="h-px bg-[rgba(26,24,22,0.08)] my-1 mx-[6px]"></div>` +
      this.menuItemHtml("delete", "Excluir", true)
    );
  }

  folderMenuHtml() {
    return (
      this.menuItemHtml("open", "Abrir pasta") +
      this.menuItemHtml("rename", "Renomear") +
      `<div class="h-px bg-[rgba(26,24,22,0.08)] my-1 mx-[6px]"></div>` +
      this.menuItemHtml("delete", "Excluir pasta", true)
    );
  }

  bindMenuActions(menu, item, kind) {
    menu.querySelectorAll("[data-act]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.stopPropagation();
        const action = button.dataset.act;
        this.closeMenu();
        if (kind === "folder") this.folderAction(action, item);
        else this.fileAction(action, item);
      });
    });
  }

  fileAction(action, item) {
    if (action === "preview") this.openPreview(item);
    else if (action === "download") window.open(item.dataset.fileUrl, "_blank");
    else if (action === "move") this.openMoveMenu(item);
    else if (action === "delete") this.deleteFile(item);
  }

  folderAction(action, item) {
    if (action === "open") this.navigate(item.dataset.openUrl);
    else if (action === "rename") this.renameFolder(item);
    else if (action === "delete") this.deleteFolder(item);
  }

  openMoveMenu(item) {
    const menu = document.createElement("div");
    menu.className = "fm-ctx fixed z-[80] min-w-[184px] p-1 rounded-[10px] bg-white shadow-[0_6px_24px_-8px_rgba(26,24,22,0.18)] border border-[rgba(26,24,22,0.14)]";
    let html = `<div class="px-[10px] pt-[6px] pb-[3px] text-[10px] uppercase tracking-wider text-[#8a847d]">Mover para</div>`;
    html += this.menuItemHtml("__root", "Raiz da ficha");
    this.itemTargets
      .filter((candidate) => candidate.dataset.itemKind === "folder")
      .forEach((folder) => {
        html += `<button type="button" data-folder="${folder.dataset.folderId}" class="fm-ctx-item flex w-full items-center gap-[10px] px-[10px] py-[7px] rounded-md text-[12.5px] text-left hover:bg-[rgba(26,24,22,0.04)]">${folder.dataset.folderName}</button>`;
      });
    menu.innerHTML = html;
    document.body.appendChild(menu);
    this.menuElement = menu;

    menu.querySelectorAll("[data-folder], [data-act='__root']").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.stopPropagation();
        this.closeMenu();
        this.moveFile(item.dataset.moveUrl, button.dataset.folder || "", item.dataset.fileName);
      });
    });
    const itemRect = item.getBoundingClientRect();
    this.positionMenu(menu, itemRect.right, itemRect.top);
  }

  async renameFolder(item) {
    const newName = prompt("Renomear pasta", item.dataset.folderName);
    if (!newName || !newName.trim()) return;
    const body = new FormData();
    body.append("attachment_folder[name]", newName.trim());
    if (this.currentFolderIdValue) body.append("folder_id", this.currentFolderIdValue);
    await patch(item.dataset.updateUrl, { body, responseKind: "turbo-stream" });
    this.toast("Pasta renomeada");
  }

  async deleteFolder(item) {
    if (!confirm(`Excluir a pasta “${item.dataset.folderName}”? Os arquivos voltam para a raiz.`)) return;
    await destroy(item.dataset.destroyUrl, { responseKind: "turbo-stream" });
    this.toast("Pasta excluída");
  }

  async deleteFile(item) {
    if (!confirm(`Excluir “${item.dataset.fileName}”?`)) return;
    await destroy(item.dataset.destroyUrl, { responseKind: "turbo-stream" });
    this.toast("Arquivo excluído");
  }

  // ---------- preview (delegated to the shared file-preview controller) ----------
  openPreview(item) {
    document.dispatchEvent(new CustomEvent("file-preview:open", { detail: { ...item.dataset } }));
  }

  // ---------- toast ----------
  toast(message, loading = false) {
    clearTimeout(this.toastTimeout);
    let toastElement = document.getElementById("fm-toast");
    if (!toastElement) {
      toastElement = document.createElement("div");
      toastElement.id = "fm-toast";
      toastElement.className = "fixed left-1/2 bottom-6 -translate-x-1/2 z-[95] inline-flex items-center gap-[10px] px-4 py-[10px] rounded-full text-[13px] font-medium text-white bg-[#1a1816] shadow-[0_14px_34px_-12px_rgba(0,0,0,0.4)]";
      document.body.appendChild(toastElement);
    }
    const indicator = loading
      ? `<span class="fm-spin w-[14px] h-[14px] rounded-full border-2 border-white/25 border-t-white"></span>`
      : `<span class="w-[7px] h-[7px] rounded-full bg-[#c85a3e]"></span>`;
    toastElement.innerHTML = `${indicator}${message}`;
    toastElement.style.opacity = "1";
    if (!loading) this.toastTimeout = setTimeout(() => { toastElement.style.opacity = "0"; }, 2600);
  }
}
